// @ts-nocheck - Class component types incompatible with React 18 + TS5
import './SignatureComponent.scss';

import * as Sentry from '@sentry/react';
import { Button } from 'antd';
import { dataURItoBlob } from 'helpers/fileHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';

import { FlexRow, ModalFooter } from '..';

interface SignatureComponentProps {
  saveSignature: (signature: FormData, reportStage: (stage: string) => void) => Promise<unknown>;
  fileName?: string;
  diagnosticSource: string;
}

const SignatureComponent = ({
  saveSignature,
  fileName,
  diagnosticSource
}: SignatureComponentProps) => {
  const { t } = useCustomTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const signaturePad = useRef<SignatureCanvas>(null);
  const diagnosticTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(diagnosticTimer.current), []);

  const clear = () => {
    signaturePad.current?.clear();
  };

  const save = async () => {
    setIsLoading(true);
    const startedAt = Date.now();
    let stage = 'canvas';
    const diagnosticContext = () => ({
      tags: { feature: 'signature-save', source: diagnosticSource, stage },
      extra: { elapsedMs: Date.now() - startedAt, online: navigator.onLine }
    });
    const reportStage = (nextStage: string) => {
      stage = nextStage;
      Sentry.addBreadcrumb({
        category: 'signature-save',
        message: stage,
        data: { source: diagnosticSource, elapsedMs: Date.now() - startedAt }
      });
    };
    reportStage('canvas');
    diagnosticTimer.current = setTimeout(() => {
      Sentry.captureMessage('Signature save is taking longer than 30 seconds', {
        ...diagnosticContext(),
        level: 'warning'
      });
    }, 30000);

    try {
      const dataUrl = signaturePad.current?.getTrimmedCanvas().toDataURL();

      if (!dataUrl) {
        Sentry.captureMessage('Signature canvas is unavailable', diagnosticContext());
        return;
      }

      reportStage('image-conversion');
      const blob = dataURItoBlob(dataUrl);

      const formData = new FormData();
      formData.append(fileName || 'file', blob, 'signature.png');

      reportStage('saving');
      await saveSignature(formData, reportStage);
      reportStage('completed');
    } catch (error) {
      // Avoid attaching API errors that may contain the signature request payload.
      Sentry.captureException(new Error('Signature save failed'), {
        ...diagnosticContext(),
        extra: {
          ...diagnosticContext().extra,
          errorType: error instanceof Error ? error.name : typeof error
        }
      });
    } finally {
      clearTimeout(diagnosticTimer.current);
      setIsLoading(false);
    }
  };

  return (
    <div className="UserSignature">
      <SignatureCanvas
        penColor="black"
        canvasProps={{
          width: 490,
          height: 200,
          className: 'UserSignature_canvas'
        }}
        ref={signaturePad}
      />
      <ModalFooter>
        <FlexRow noMargin>
          <Button onClick={clear} danger>
            {t('settings.users.clearSignatureButton')}
          </Button>
          <Button onClick={save} type="primary" loading={isLoading}>
            {t('settings.users.saveSignatureButton')}
          </Button>
        </FlexRow>
      </ModalFooter>
    </div>
  );
};

export default SignatureComponent;
